import { z } from "zod";
import { PLATFORMS } from "./platforms.js";

export const platformEnum = z.enum(PLATFORMS);

/* ---------- media ---------- */

export const createMediaInput = z.object({
  filename: z.string().min(1).max(255),
  mime: z.string().min(3).max(100),
  bytes: z.number().int().positive().max(5 * 1024 * 1024 * 1024),
  kind: z.enum(["image", "video"]),
});
export type CreateMediaInput = z.infer<typeof createMediaInput>;

export const completeMediaInput = z.object({
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  durationMs: z.number().int().positive().optional(),
  checksumSha256: z.string().regex(/^[a-f0-9]{64}$/i).optional(),
});
export type CompleteMediaInput = z.infer<typeof completeMediaInput>;

/* ---------- posts ---------- */

export const igMediaType = z.enum(["FEED", "REELS", "STORIES"]);
export const tiktokPrivacy = z.enum([
  "PUBLIC_TO_EVERYONE",
  "MUTUAL_FOLLOW_FRIENDS",
  "FOLLOWER_OF_CREATOR",
  "SELF_ONLY",
]);

export const targetOptions = z
  .object({
    igMediaType: igMediaType.optional(),
    tiktokPrivacyLevel: tiktokPrivacy.optional(),
    tiktokDisableComment: z.boolean().optional(),
    tiktokDisableDuet: z.boolean().optional(),
    tiktokDisableStitch: z.boolean().optional(),
    facebookLinkUrl: z.string().url().optional(),
  })
  .strict();
export type TargetOptions = z.infer<typeof targetOptions>;

export const postTargetInput = z.object({
  connectionId: z.string().min(1),
  options: targetOptions.default({}),
});

export const createPostInput = z
  .object({
    caption: z.string().max(63206).default(""),
    mediaAssetIds: z.array(z.string().min(1)).max(35).default([]),
    targets: z.array(postTargetInput).min(1).max(20),
    // ISO 8601 UTC instant; omit / null for a draft
    scheduledAt: z.string().datetime({ offset: true }).nullable().optional(),
    timezone: z.string().min(1).max(64).default("UTC"),
    status: z.enum(["draft", "scheduled"]).default("scheduled"),
  })
  .refine((v) => v.status === "draft" || !!v.scheduledAt, {
    message: "scheduledAt is required unless status is 'draft'",
    path: ["scheduledAt"],
  });
export type CreatePostInput = z.infer<typeof createPostInput>;

export const updatePostInput = z.object({
  caption: z.string().max(63206).optional(),
  mediaAssetIds: z.array(z.string().min(1)).max(35).optional(),
  targets: z.array(postTargetInput).min(1).max(20).optional(),
  scheduledAt: z.string().datetime({ offset: true }).nullable().optional(),
  timezone: z.string().min(1).max(64).optional(),
});
export type UpdatePostInput = z.infer<typeof updatePostInput>;

/* ---------- org ---------- */

export const createOrgInput = z.object({
  name: z.string().min(1).max(120),
  slug: z
    .string()
    .min(2)
    .max(48)
    .regex(/^[a-z0-9-]+$/, "lowercase letters, numbers and dashes only")
    .optional(),
});
export type CreateOrgInput = z.infer<typeof createOrgInput>;
